import { useEffect, useState } from "react";
import axios from "axios";
import { Instagram, Play, Film, Sparkles, ArrowRight, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COVER_URL = (fid) => fid ? `${API}/rivoluzione/cover/${fid}` : null;

const TIPO_ICON = { reel: Film, pillola: Play, carosello: Sparkles, post: Instagram };

/**
 * Striscia editoriale "Video della Redazione su Instagram"
 * Stile news-hub (Geopop-like): i contenuti sono dell'agenzia, il volto e' Christian
 * ma il brand e' @colucci_assicuratori.
 *
 * props:
 * - variant: "dark" (su fondo scuro) | "light" (su fondo chiaro, default)
 * - title: titolo sopra la striscia
 * - subtitle: sottotitolo/payoff
 * - limit: max contenuti mostrati (default 4)
 */
export default function InstagramRedazioneStrip({
  variant = "light",
  title = "Le news in video sul nostro Instagram",
  subtitle = "La redazione riassume in 60 secondi le notizie piu' importanti della settimana. Seguici su @colucci_assicuratori.",
  limit = 4,
}) {
  const [profile, setProfile] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/rivoluzione/profilo`).then(r => setProfile(r.data)).catch(() => {});
    axios.get(`${API}/rivoluzione/contenuti`, { params: { limit } })
      .then(r => setItems((r.data || []).slice(0, limit)))
      .catch(() => setItems([]));
  }, [limit]);

  const igUrl = profile?.instagram_url || "https://www.instagram.com/colucci_assicuratori/";
  const handle = profile?.handle || "colucci_assicuratori";
  const isDark = variant === "dark";

  return (
    <section className={`relative ${isDark ? "bg-[#0F1F14] text-white" : "bg-white text-zinc-900"}`} data-testid="ig-redazione-strip">
      {/* Top band con handle */}
      <div className={`border-b ${isDark ? "border-white/5" : "border-zinc-100"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-[#FEDA77] via-[#F58529] via-40% to-[#DD2A7B] p-0.5">
                  <div className={`w-full h-full rounded-full flex items-center justify-center ${isDark ? "bg-[#0F1F14]" : "bg-white"}`}>
                    <Instagram className="w-4 h-4" />
                  </div>
                </div>
                <div>
                  <p className={`text-[11px] font-bold uppercase tracking-[0.2em] ${isDark ? "text-[#D97B58]" : "text-[#D97B58]"}`}>Canale Redazione</p>
                  <a href={igUrl} target="_blank" rel="noopener noreferrer"
                    className={`text-sm font-semibold hover:opacity-80 transition-opacity ${isDark ? "text-white" : "text-zinc-900"}`}
                    data-testid="ig-strip-handle">
                    @{handle}
                  </a>
                </div>
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight mb-3">
                {title}
              </h2>
              <p className={`text-sm sm:text-base leading-relaxed ${isDark ? "text-white/60" : "text-zinc-500"}`}>
                {subtitle}
              </p>
            </div>
            <a href={igUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-gradient-to-tr from-[#FEDA77] via-[#F58529] via-40% to-[#DD2A7B] text-white text-sm font-semibold hover:scale-[1.02] transition-all shadow-lg shadow-[#DD2A7B]/20 shrink-0"
              data-testid="ig-strip-follow">
              <Instagram className="w-4 h-4" /> Segui su Instagram
              <ExternalLink className="w-3.5 h-3.5 opacity-70" />
            </a>
          </div>
        </div>
      </div>

      {/* Strip */}
      {items.length > 0 ? (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="ig-strip-grid">
            {items.map((it) => {
              const Icon = TIPO_ICON[it.tipo] || Film;
              const coverUrl = COVER_URL(it.cover_file_id);
              const linkOut = it.link_instagram || igUrl;
              return (
                <a key={it.id} href={linkOut} target="_blank" rel="noopener noreferrer"
                  className={`group relative aspect-[4/5] rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${isDark ? "border-white/10" : "border-zinc-200"}`}
                  data-testid={`ig-strip-item-${it.id}`}>
                  {coverUrl ? (
                    <img src={coverUrl} alt={it.titolo} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0F1F14] via-[#1d4a2e] to-[#D97B58]" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />

                  <div className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/95 text-[9px] font-bold uppercase tracking-wider text-zinc-900">
                    <Icon className="w-3 h-3" /> {it.tipo}
                  </div>

                  {(it.tipo === "reel" || it.tipo === "pillola") && (
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-5 h-5 text-white fill-white" />
                    </div>
                  )}

                  <div className="absolute bottom-0 left-0 right-0 p-3.5">
                    <p className="text-[9px] uppercase tracking-wider text-white/70 font-semibold mb-1">{it.categoria}</p>
                    <h3 className="font-heading text-sm font-bold text-white leading-tight line-clamp-2">{it.titolo}</h3>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
          <div className={`rounded-2xl border-2 border-dashed p-10 text-center ${isDark ? "border-white/10" : "border-zinc-200"}`}>
            <Film className={`w-8 h-8 mx-auto mb-3 ${isDark ? "text-white/30" : "text-zinc-300"}`} />
            <p className={`text-sm mb-4 ${isDark ? "text-white/50" : "text-zinc-500"}`}>
              La redazione sta preparando i prossimi video. Segui <strong>@{handle}</strong> per non perdertene uno.
            </p>
            <a href={igUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-gradient-to-tr from-[#FEDA77] via-[#F58529] to-[#DD2A7B] text-white text-xs font-semibold hover:scale-[1.02] transition-all">
              <Instagram className="w-3.5 h-3.5" /> Apri Instagram <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      )}
    </section>
  );
}
